import React, { useEffect } from 'react';
import { Alert, Col, Form, Input, Row, Select, Typography, Input as AntInput } from 'antd';
import type { FormInstance } from 'antd';
import type { ContactDetail, ContactFormValues, ContactType } from '../types';
import { normalizeContactType } from '../contactType';

const { TextArea } = AntInput;
const { Title } = Typography;

export interface ContactFormProps {
  form: FormInstance<ContactFormFieldValues>;
  initialValues?: ContactDetail | null;
  ownerOptions: { value: string; label: string }[];
  categoryOptions: { value: number; label: string }[];
  roleOptions: { value: number; label: string }[];
  loadingCategories?: boolean;
  loadingOwners?: boolean;
  loadingRoles?: boolean;
  onFinish: (values: ContactFormValues) => void;
}

const contactTypeOptions: { value: ContactType; label: string }[] = [
  { value: 0, label: 'Individual' },
  { value: 1, label: 'Organization' },
];

function isValidCategoryId(
  categoryId: number | undefined | null,
  categoryOptions: { value: number; label: string }[]
): boolean {
  return (
    categoryId != null &&
    categoryId > 0 &&
    categoryOptions.some((option) => option.value === categoryId)
  );
}

export type ContactFormFieldValues = ContactFormValues

function contactDetailToFormValues(d: ContactDetail): ContactFormFieldValues {
  return {
    categoryId: d.categoryId,
    contactType: normalizeContactType(d.contactType),
    role: d.role,
    name: d.name ?? undefined,
    companyName: d.companyName ?? undefined,
    jobTitle: d.jobTitle ?? undefined,
    mobileNumber: d.mobileNumber ?? undefined,
    email: d.email ?? undefined,
    whatsAppNumber: d.whatsAppNumber ?? undefined,
    website: d.website ?? undefined,
    linkedIn: d.linkedIn ?? undefined,
    address: d.address ?? undefined,
    googleMapsLink: d.googleMapsLink ?? undefined,
    ownerUserId: d.ownerUserId ?? undefined,
    tags: d.tags?.length ? d.tags : undefined,
    notes: d.notes ?? undefined,
  };
}

const ContactForm: React.FC<ContactFormProps> = ({
  form,
  initialValues,
  ownerOptions,
  categoryOptions,
  roleOptions,
  loadingCategories = false,
  loadingOwners = false,
  loadingRoles = false,
  onFinish,
}) => {
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(contactDetailToFormValues(initialValues));
      return;
    }
    form.resetFields();
    form.setFieldsValue({ contactType: 0 });
  }, [initialValues, form]);

  useEffect(() => {
    if (initialValues || categoryOptions.length === 0) {
      return;
    }
    const categoryId = form.getFieldValue('categoryId') as number | undefined;
    if (!isValidCategoryId(categoryId, categoryOptions)) {
      form.setFieldValue('categoryId', categoryOptions[0].value);
    }
  }, [initialValues, categoryOptions, form]);

  const handleFinish = (v: ContactFormFieldValues) => {
    if (!isValidCategoryId(v.categoryId, categoryOptions)) {
      form.setFields([{ name: 'categoryId', errors: ['Please select category'] }]);
      return;
    }
    const values: ContactFormValues = { ...v, role: v.role ?? null };
    onFinish(values);
  };

  return (
    <Form<ContactFormFieldValues>
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ contactType: 0 }}
    >
      {categoryOptions.length === 0 && !loadingCategories && (
        <Alert
          type="warning"
          showIcon
          message="No contact categories are available. Add at least one active category in Settings before creating a contact."
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Basic
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="contactType" label="Type">
              <Select options={contactTypeOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="categoryId"
              label="Category"
              rules={[
                {
                  validator: (_, value: number | undefined) => {
                    if (!isValidCategoryId(value, categoryOptions)) {
                      return Promise.reject(new Error('Please select category'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                placeholder="Select category"
                options={categoryOptions}
                loading={loadingCategories}
                disabled={categoryOptions.length === 0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              dependencies={['contactType']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const type = getFieldValue('contactType') as ContactType | undefined;
                    if (type === 1) {
                      return Promise.resolve();
                    }
                    if (value === undefined || value === null || String(value).trim() === '') {
                      return Promise.reject(new Error('Please enter a name'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="companyName"
              label="Company name"
              dependencies={['contactType']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const type = getFieldValue('contactType') as ContactType | undefined;
                    if (type !== 1) {
                      return Promise.resolve();
                    }
                    if (value === undefined || value === null || String(value).trim() === '') {
                      return Promise.reject(new Error('Please enter a company name'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="jobTitle" label="Job title">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="role" label="Role">
              <Select
                allowClear
                placeholder="Select role"
                options={roleOptions}
                loading={loadingRoles}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Communication
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="mobileNumber" label="Mobile number">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label="Email">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="whatsAppNumber" label="WhatsApp number">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="website" label="Website">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="linkedIn" label="LinkedIn">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Location
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="address" label="Address">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="googleMapsLink" label="Google Maps link">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Ownership & tags
        </Title>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="ownerUserId" label="Owner">
              <Select
                allowClear
                placeholder="Select owner"
                options={ownerOptions}
                loading={loadingOwners}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="tags" label="Tags">
              <Select mode="tags" placeholder="Add tags" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="cp-form-section">
        <Title level={5} style={{ marginTop: 0 }}>
          Notes
        </Title>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={4} />
        </Form.Item>
      </div>
    </Form>
  );
};

export default ContactForm;
